import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "../../api/http";
import { DEFAULT_PLAYER_IMAGE } from "../../components/DraftOdds/DraftOddsComponents";
import { buildPlayerPagePath } from "../Player/playerTabs";
import {
    actualOverallPick,
    formatPointsPerGame,
    formatSavePct,
    formatSignedScore,
    formatStatValue,
    groupPicksByTeam,
    parseNhlStatsResponse,
    RECENT_DRAFT_PROSPECT_YEARS,
    scoreDraftTeams,
    summarizeNhlCareer,
    USER_TEAM_INDEX,
} from "../../utils/blindDraft";

function PlayerProfileLink({ playerId, className = "", children }) {
    if (!playerId) {
        return <span className={className}>{children}</span>;
    }

    return (
        <Link to={buildPlayerPagePath(playerId)} className={`blind-draft-player-profile-link ${className}`.trim()}>
            {children}
        </Link>
    );
}

function PlayerProfileButton({ playerId, className = "", asSpan = false }) {
    if (!playerId) return null;

    if (asSpan) {
        return <span className={`blind-draft-player-page-link ${className}`.trim()}>View profile</span>;
    }

    return (
        <Link to={buildPlayerPagePath(playerId)} className={`blind-draft-player-page-link ${className}`.trim()}>
            View profile
        </Link>
    );
}

function NhlDraftPickInfo({ nhlDraft, className = "" }) {
    if (!nhlDraft?.year) {
        return (
            <p className={`blind-draft-nhl-draft-info is-empty ${className}`.trim()}>
                NHL draft info unavailable
            </p>
        );
    }

    return (
        <p className={`blind-draft-nhl-draft-info ${className}`.trim()}>
            <span className="blind-draft-nhl-draft-label">Actual NHL draft</span>
            {nhlDraft.year}
            {nhlDraft.overallPick != null && (
                <>
                    {" · #"}
                    {nhlDraft.overallPick}
                    {" overall"}
                </>
            )}
            {nhlDraft.round != null && nhlDraft.pick != null && (
                <>
                    {" · R"}
                    {nhlDraft.round}
                    {" P"}
                    {nhlDraft.pick}
                </>
            )}
            {nhlDraft.teamName && (
                <>
                    {" · "}
                    {nhlDraft.teamName}
                </>
            )}
        </p>
    );
}

function NhlCareerSummary({ summary, isGoalie }) {
    if (!summary) {
        return <p className="blind-draft-result-nhl-empty">No NHL career stats available.</p>;
    }

    if (isGoalie) {
        return (
            <p className="blind-draft-result-nhl-line">
                NHL: {summary.gamesPlayed || 0} GP · {formatSavePct(summary.savePercentage)} SV% · {formatStatValue(summary.goalsAgainstAverage?.toFixed?.(2) ?? summary.goalsAgainstAverage)} GAA
            </p>
        );
    }

    return (
        <p className="blind-draft-result-nhl-line">
            NHL: {summary.gamesPlayed || 0} GP · {summary.goals || 0} G · {summary.assists || 0} A · {summary.points || 0} PTS · {formatPointsPerGame(summary.pointsPerGame)} PPG
        </p>
    );
}

function RevealCard({
    pick,
    identity,
    nhlSummary,
    index,
    isRevealed,
    showContent,
}) {
    const isGoalie = pick.prospect.isGoalie;
    const imageUrl = identity?.playerImageUrl || DEFAULT_PLAYER_IMAGE;
    const playerPath = showContent && identity?.playerId ? buildPlayerPagePath(identity.playerId) : null;
    const CardTag = playerPath ? Link : "article";
    const cardProps = playerPath
        ? {
            to: playerPath,
            className: `blind-draft-reveal-card blind-draft-reveal-card--link ${isRevealed ? "is-visible" : ""} ${showContent ? "is-revealed" : ""}`,
            style: { animationDelay: `${index * 0.12}s` },
        }
        : {
            className: `blind-draft-reveal-card ${isRevealed ? "is-visible" : ""} ${showContent ? "is-revealed" : ""}`,
            style: { animationDelay: `${index * 0.12}s` },
        };

    return (
        <CardTag {...cardProps}>
            <div className="blind-draft-reveal-card-media">
                <img
                    src={imageUrl}
                    alt={showContent ? identity?.realName : pick.prospect.fakeName}
                    className={`blind-draft-reveal-image ${showContent ? "is-visible" : ""}`}
                />
            </div>
            <div className="blind-draft-reveal-card-body">
                <span className="blind-draft-badge">Your Pick {pick.pickNumber}</span>
                <div className="blind-draft-reveal-name-stack">
                    <p className="blind-draft-reveal-fake">{pick.prospect.fakeName}</p>
                    <h3 className={`blind-draft-reveal-real ${showContent ? "is-visible" : ""}`}>
                        {identity?.realName || "Unknown Player"}
                    </h3>
                </div>
                <p className="blind-draft-reveal-meta">
                    {pick.prospect.position}
                </p>
                {showContent && (
                    <NhlDraftPickInfo
                        nhlDraft={identity?.draftResult
                            ? {
                                year: identity.draftResult.draft_year,
                                round: identity.draftResult.round,
                                pick: identity.draftResult.pick,
                                overallPick: actualOverallPick(identity.draftResult),
                                teamName: identity.draftResult.team_name,
                            }
                            : null}
                    />
                )}
                {showContent && (
                    <div className="blind-draft-reveal-nhl">
                        <NhlCareerSummary summary={nhlSummary} isGoalie={isGoalie} />
                    </div>
                )}
                {showContent && <PlayerProfileButton playerId={identity?.playerId} asSpan />}
            </div>
        </CardTag>
    );
}

function pickValueKindLabel(kind) {
    if (kind === "steal") return "steal";
    if (kind === "reach") return "reach";
    return "aligned";
}

function CareerBreakdownDetail({ career, labelPrefix = "NHL" }) {
    const { components, observed, maturity, yearsSinceDraft, score } = career;

    if (!components) {
        return <p className="blind-draft-score-breakdown-detail">No {labelPrefix} stats on file → 0 pts</p>;
    }

    let rawFormula = "";
    if (components.type === "goalie") {
        rawFormula = (
            <>
                {labelPrefix}: {components.gamesPlayed} GP × 0.12 ({formatSignedScore(components.gpContribution)})
                {" + "}
                {formatSavePct(components.savePercentage)} SV% above 0.88 × 120 ({formatSignedScore(components.svContribution)})
                {" + "}
                GAA below 2.85 × 18 ({formatSignedScore(components.gaaContribution)})
                {" = raw "}
                {formatSignedScore(observed)}
            </>
        );
    } else {
        rawFormula = (
            <>
                {labelPrefix}: {components.gamesPlayed} GP × 0.06 ({formatSignedScore(components.gpContribution)})
                {" + "}
                {components.points} PTS × 0.45 ({formatSignedScore(components.pointsContribution)})
                {" + "}
                {formatPointsPerGame(components.pointsPerGame)} PPG × 22 ({formatSignedScore(components.ppgContribution)})
                {" = raw "}
                {formatSignedScore(observed)}
            </>
        );
    }

    let projectionLine = `Impact score = ${formatSignedScore(score)} pts`;
    if (components.multiplier != null) {
        projectionLine = `Raw ${formatSignedScore(observed)} × ${components.multiplier} = ${formatSignedScore(score)} pts`;
    } else if (maturity < 1 && yearsSinceDraft != null) {
        projectionLine = `${yearsSinceDraft} yrs since draft → maturity ${maturity.toFixed(2)} → raw ${formatSignedScore(observed)} ÷ ${maturity.toFixed(2)} = ${formatSignedScore(score)} pts`;
    }

    return (
        <>
            <p className="blind-draft-score-breakdown-detail">{rawFormula}</p>
            <p className="blind-draft-score-breakdown-detail">{projectionLine}</p>
        </>
    );
}

function NhlCareerBreakdownDetail({ nhlCareer }) {
    return <CareerBreakdownDetail career={nhlCareer} labelPrefix="NHL" />;
}

function ProspectDevelopmentBreakdownDetail({ prospectDevelopment }) {
    if (!prospectDevelopment) return null;
    return (
        <CareerBreakdownDetail
            career={prospectDevelopment}
            labelPrefix="Post-draft non-NHL"
        />
    );
}

function ProspectBonusBreakdownDetail({ prospectBonus }) {
    if (!prospectBonus?.eligible) {
        return (
            <p className="blind-draft-score-breakdown-detail">
                Not eligible for recent-prospect bonus
            </p>
        );
    }

    return (
        <p className="blind-draft-score-breakdown-detail">
            {prospectBonus.years} yrs since draft · pick factor {prospectBonus.pickFactor}
            {" · recency "}
            {prospectBonus.recencyFactor}
            {" · NHL fade "}
            {prospectBonus.nhlFade}
            {" = "}
            {formatSignedScore(prospectBonus.score)} pts
        </p>
    );
}

function PickScoreBreakdown({ row }) {
    const { breakdown, pickValueScore, nhlScore, prospectScore, total } = row;
    const pick = breakdown.pickValue;
    const nhlCareer = breakdown.nhlCareer;
    const prospectDevelopment = breakdown.prospectDevelopment;
    const prospectBonus = breakdown.prospectBonus;
    const hasProspectSections = row.isRecentProspect;

    return (
        <div className="blind-draft-score-breakdown-panel">
            <div className="blind-draft-score-breakdown-section">
                <div className="blind-draft-score-breakdown-row">
                    <span>Draft efficiency</span>
                    <span className={pickValueScore >= 0 ? "is-positive" : "is-negative"}>
                        {formatSignedScore(pickValueScore)} pts
                    </span>
                </div>
                {pick.actualPick != null ? (
                    <p className="blind-draft-score-breakdown-detail">
                        Mock #{pick.mockPick} − NHL #{pick.actualPick} = {formatSignedScore(pick.pickDelta)} slots
                        {pick.pickDelta !== 0 && ` (${pickValueKindLabel(pick.kind)})`}
                        {" × "}
                        {pick.multiplier}
                        {" = "}
                        {formatSignedScore(pickValueScore)} pts
                    </p>
                ) : (
                    <p className="blind-draft-score-breakdown-detail">No NHL draft slot on file → 0 pts</p>
                )}
            </div>

            <div className="blind-draft-score-breakdown-section">
                <div className="blind-draft-score-breakdown-row">
                    <span>NHL career impact</span>
                    <span className={nhlScore >= 0 ? "is-positive" : "is-negative"}>
                        {formatSignedScore(nhlScore)} pts
                    </span>
                </div>
                <NhlCareerBreakdownDetail nhlCareer={nhlCareer} />
            </div>

            {hasProspectSections && (
                <>
                    <div className="blind-draft-score-breakdown-section">
                        <div className="blind-draft-score-breakdown-row">
                            <span>Prospect development (non-NHL)</span>
                            <span className={(prospectDevelopment?.score || 0) >= 0 ? "is-positive" : "is-negative"}>
                                {formatSignedScore(prospectDevelopment?.score || 0)} pts
                            </span>
                        </div>
                        <ProspectDevelopmentBreakdownDetail prospectDevelopment={prospectDevelopment} />
                    </div>

                    <div className="blind-draft-score-breakdown-section">
                        <div className="blind-draft-score-breakdown-row">
                            <span>Recent-prospect bonus</span>
                            <span className={(prospectBonus?.score || 0) >= 0 ? "is-positive" : "is-negative"}>
                                {formatSignedScore(prospectBonus?.score || 0)} pts
                            </span>
                        </div>
                        <ProspectBonusBreakdownDetail prospectBonus={prospectBonus} />
                    </div>
                </>
            )}

            <div className="blind-draft-score-breakdown-total">
                <span>Pick total</span>
                <span>
                    {formatSignedScore(pickValueScore)}
                    {" + "}
                    {formatSignedScore(nhlScore)}
                    {hasProspectSections && (
                        <>
                            {" + "}
                            {formatSignedScore(prospectScore)}
                        </>
                    )}
                    {" = "}
                    <strong>{formatSignedScore(total)} pts</strong>
                </span>
            </div>
        </div>
    );
}

function RankingPickRow({ row, teamIndex, isExpanded, onToggle }) {
    const expandId = `blind-draft-pick-breakdown-${teamIndex}-${row.pick.pickNumber}`;

    return (
        <li className={`blind-draft-ranking-pick ${isExpanded ? "is-expanded" : ""}`}>
            <div className="blind-draft-ranking-pick-header">
                <div className="blind-draft-ranking-pick-title">
                    <PlayerProfileLink
                        playerId={row.playerId}
                        className="blind-draft-ranking-pick-name"
                    >
                        {row.realName || row.pick.prospect.fakeName}
                    </PlayerProfileLink>
                    <span className="blind-draft-ranking-pick-mock">
                        Mock pick #{row.mockPick}
                    </span>
                </div>
                <div className="blind-draft-ranking-pick-actions">
                    <span className="blind-draft-ranking-pick-score">
                        {row.total} pts
                    </span>
                    <PlayerProfileButton
                        playerId={row.playerId}
                        className="blind-draft-player-page-link--inline"
                    />
                </div>
            </div>

            <NhlDraftPickInfo nhlDraft={row.nhlDraft} />

            <button
                type="button"
                className="blind-draft-breakdown-toggle"
                aria-expanded={isExpanded}
                aria-controls={expandId}
                onClick={onToggle}
            >
                {isExpanded ? "Hide score breakdown" : "Show score breakdown"}
            </button>

            {isExpanded && (
                <div id={expandId} className="blind-draft-ranking-pick-breakdown">
                    <PickScoreBreakdown row={row} />
                </div>
            )}
        </li>
    );
}

function DraftRankings({ rankings, loading }) {
    const [expandedPickKey, setExpandedPickKey] = useState(null);

    const togglePickBreakdown = (teamIndex, pickNumber) => {
        const key = `${teamIndex}-${pickNumber}`;
        setExpandedPickKey((current) => (current === key ? null : key));
    };

    if (loading) {
        return (
            <section className="blind-draft-rankings">
                <h3>Draft Grades</h3>
                <p className="blind-draft-rankings-loading">Calculating team scores…</p>
            </section>
        );
    }

    return (
        <section className="blind-draft-rankings">
            <div className="blind-draft-rankings-header">
                <div>
                    <h3>Draft Grades</h3>
                    <p>
                        Teams ranked by draft efficiency (steals vs reaches compared to real NHL slots) plus
                        projected NHL career impact. Players drafted within the last {RECENT_DRAFT_PROSPECT_YEARS}
                        {" "}
                        years also receive credit for post-draft non-NHL production and a prospect bonus when
                        NHL sample size is still limited.
                    </p>
                </div>
            </div>
            <div className="blind-draft-rankings-list">
                {rankings.map((team) => (
                    <article
                        key={team.teamIndex}
                        className={`blind-draft-ranking-card ${team.teamIndex === USER_TEAM_INDEX ? "is-user" : ""} ${team.rank === 1 ? "is-first" : ""}`}
                        style={{ animationDelay: `${team.rank * 0.06}s` }}
                    >
                        <div className="blind-draft-ranking-place">#{team.rank}</div>
                        <div className="blind-draft-ranking-main">
                            <div className="blind-draft-ranking-top">
                                <strong>{team.teamName}</strong>
                                <span className="blind-draft-ranking-score">{team.totalScore} pts</span>
                            </div>
                            <ul className="blind-draft-ranking-picks">
                                {team.pickScores.map((row) => {
                                    const pickKey = `${team.teamIndex}-${row.pick.pickNumber}`;
                                    return (
                                        <RankingPickRow
                                            key={row.pick.pickNumber}
                                            row={row}
                                            teamIndex={team.teamIndex}
                                            isExpanded={expandedPickKey === pickKey}
                                            onToggle={() => togglePickBreakdown(team.teamIndex, row.pick.pickNumber)}
                                        />
                                    );
                                })}
                            </ul>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}

function FullDraftBoard({ picksByTeam, selectedTeamIndex, onSelectTeam, identityMap, playerStatsByPlayerId }) {
    const selectedTeam = picksByTeam[selectedTeamIndex];

    return (
        <section className="blind-draft-full-board">
            <div className="blind-draft-full-board-header">
                <div>
                    <h3>Full Draft Results</h3>
                    <p>Browse every pick by fictional team — all players revealed.</p>
                </div>
                <div className="blind-draft-team-tabs" role="tablist" aria-label="Draft teams">
                    {picksByTeam.map((team) => (
                        <button
                            key={team.teamIndex}
                            type="button"
                            role="tab"
                            aria-selected={selectedTeamIndex === team.teamIndex}
                            className={`blind-draft-team-tab ${selectedTeamIndex === team.teamIndex ? "is-active" : ""} ${team.teamIndex === USER_TEAM_INDEX ? "is-user" : ""}`}
                            onClick={() => onSelectTeam(team.teamIndex)}
                        >
                            {team.teamName}
                        </button>
                    ))}
                </div>
            </div>
            <div className="blind-draft-full-board-table-wrap">
                <table className="blind-draft-full-board-table">
                    <thead>
                        <tr>
                            <th>Pick</th>
                            <th>Round</th>
                            <th>Player</th>
                            <th>Pos</th>
                            <th>NHL Draft</th>
                            <th>NHL GP</th>
                            <th>NHL PTS</th>
                            <th>NHL PPG</th>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedTeam.picks.map((pick) => {
                            const identity = identityMap.get(pick.prospect.anonId);
                            const playerId = identity?.playerId;
                            const statsRows = playerId ? (playerStatsByPlayerId[playerId] ?? []) : [];
                            const nhlSummary = statsRows.length
                                ? summarizeNhlCareer(statsRows, pick.prospect.isGoalie)
                                : null;
                            const nhlDraft = identity?.draftResult
                                ? {
                                    year: identity.draftResult.draft_year,
                                    round: identity.draftResult.round,
                                    pick: identity.draftResult.pick,
                                    overallPick: actualOverallPick(identity.draftResult),
                                    teamName: identity.draftResult.team_name,
                                }
                                : null;
                            const isUserTeam = selectedTeam.teamIndex === USER_TEAM_INDEX;
                            const realName = identity?.realName;
                            return (
                                <tr key={pick.pickNumber} className={isUserTeam ? "is-user-pick" : ""}>
                                    <td>{pick.pickNumber}</td>
                                    <td>{pick.round}</td>
                                    <td>
                                        <div className="blind-draft-full-board-player">
                                            <PlayerProfileLink
                                                playerId={playerId}
                                                className="blind-draft-full-board-real-name"
                                            >
                                                {realName || pick.prospect.fakeName}
                                            </PlayerProfileLink>
                                            {realName && (
                                                <span className="blind-draft-full-board-fake-name">
                                                    {pick.prospect.fakeName}
                                                </span>
                                            )}
                                            <PlayerProfileButton
                                                playerId={playerId}
                                                className="blind-draft-player-page-link--inline"
                                            />
                                        </div>
                                    </td>
                                    <td>{pick.prospect.position}</td>
                                    <td>
                                        {nhlDraft?.year ? (
                                            <div className="blind-draft-full-board-nhl-draft">
                                                <span>
                                                    {nhlDraft.year}
                                                    {nhlDraft.overallPick != null && ` · #${nhlDraft.overallPick}`}
                                                </span>
                                                {nhlDraft.round != null && nhlDraft.pick != null && (
                                                    <span>
                                                        R{nhlDraft.round} P{nhlDraft.pick}
                                                    </span>
                                                )}
                                                {nhlDraft.teamName && (
                                                    <span>{nhlDraft.teamName}</span>
                                                )}
                                            </div>
                                        ) : (
                                            "—"
                                        )}
                                    </td>
                                    <td>{formatStatValue(nhlSummary?.gamesPlayed)}</td>
                                    <td>{pick.prospect.isGoalie ? "—" : formatStatValue(nhlSummary?.points)}</td>
                                    <td>{pick.prospect.isGoalie ? "—" : formatPointsPerGame(nhlSummary?.pointsPerGame)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function DraftResults({ userPicks = [], picks = [], teamNames, identityMap, onPlayAgain }) {
    const [visibleCount, setVisibleCount] = useState(0);
    const [revealedCount, setRevealedCount] = useState(0);
    const [playerStatsByPlayerId, setPlayerStatsByPlayerId] = useState({});
    const [statsLoading, setStatsLoading] = useState(true);
    const [selectedTeamIndex, setSelectedTeamIndex] = useState(USER_TEAM_INDEX);
    const [showPostReveal, setShowPostReveal] = useState(false);

    const picksByTeam = useMemo(
        () => groupPicksByTeam(picks, teamNames),
        [picks, teamNames]
    );

    const rankings = useMemo(
        () => scoreDraftTeams(picks, identityMap, playerStatsByPlayerId, teamNames),
        [picks, identityMap, playerStatsByPlayerId, teamNames]
    );

    useEffect(() => {
        setVisibleCount(0);
        setRevealedCount(0);
        setShowPostReveal(false);
        setSelectedTeamIndex(USER_TEAM_INDEX);
        setStatsLoading(true);
        setPlayerStatsByPlayerId({});
    }, [userPicks, picks]);

    useEffect(() => {
        const playerIds = [...new Set(
            picks
                .map((pick) => identityMap.get(pick.prospect.anonId)?.playerId)
                .filter(Boolean)
        )];

        if (!playerIds.length) {
            setStatsLoading(false);
            return undefined;
        }

        let cancelled = false;
        Promise.all(
            playerIds.map((playerId) => publicApi
                .get(`/player_nhl_stats/${playerId}`)
                .then((response) => [playerId, parseNhlStatsResponse(response.data)])
                .catch(() => [playerId, []]))
        ).then((entries) => {
            if (cancelled) return;
            setPlayerStatsByPlayerId(Object.fromEntries(entries));
            setStatsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [picks, identityMap]);

    useEffect(() => {
        if (visibleCount >= userPicks.length) return undefined;

        const timer = setTimeout(() => {
            setVisibleCount((count) => count + 1);
        }, 500);

        return () => clearTimeout(timer);
    }, [visibleCount, userPicks.length]);

    useEffect(() => {
        if (revealedCount >= visibleCount) return undefined;

        const timer = setTimeout(() => {
            setRevealedCount((count) => count + 1);
        }, 700);

        return () => clearTimeout(timer);
    }, [revealedCount, visibleCount]);

    useEffect(() => {
        if (userPicks.length > 0 && revealedCount >= userPicks.length) {
            const timer = setTimeout(() => setShowPostReveal(true), 500);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [revealedCount, userPicks.length]);

    return (
        <section className="blind-draft-results">
            <div className="blind-draft-phase-header">
                <div>
                    <h2>Draft Complete</h2>
                    <p>Your four picks are revealed below, followed by draft grades for every team.</p>
                </div>
                <button type="button" className="blind-draft-secondary-btn" onClick={onPlayAgain}>
                    Play Again
                </button>
            </div>

            <div className="blind-draft-reveal-grid">
                {userPicks.map((pick, index) => {
                    const identity = identityMap.get(pick.prospect.anonId);
                    const playerId = identity?.playerId;
                    const statsRows = playerId ? (playerStatsByPlayerId[playerId] ?? []) : [];
                    const nhlSummary = statsRows.length
                        ? summarizeNhlCareer(statsRows, pick.prospect.isGoalie)
                        : null;

                    return (
                        <RevealCard
                            key={pick.pickNumber}
                            pick={pick}
                            identity={identity}
                            nhlSummary={nhlSummary}
                            index={index}
                            isRevealed={index < visibleCount}
                            showContent={index < revealedCount}
                        />
                    );
                })}
            </div>

            {showPostReveal && (
                <>
                    <DraftRankings rankings={rankings} loading={statsLoading} />
                    {!statsLoading && (
                        <FullDraftBoard
                            picksByTeam={picksByTeam}
                            selectedTeamIndex={selectedTeamIndex}
                            onSelectTeam={setSelectedTeamIndex}
                            identityMap={identityMap}
                            playerStatsByPlayerId={playerStatsByPlayerId}
                        />
                    )}
                </>
            )}
        </section>
    );
}

export default DraftResults;
